import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { PackagePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { usePinFilters } from "@/lib/pins-context";
import { PinFilterBar } from "@/components/pin-filter-bar";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";

const statusBadge: Record<string, string> = {
  draft: "rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground",
  active: "rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400",
  archived: "rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

export default function PackagesPage() {
  const [selected, setSelected] = useState<PackagePublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");
  const { activeOrgIds } = usePinFilters();
  const hasPinFilter = activeOrgIds.length > 0;
  const pinKey = activeOrgIds.join(",");

  async function handleDelete(id: string) {
    if (!confirm("Are you sure you want to delete this package?")) return;
    try {
      await api.deletePackage(id);
      if (selected?.id === id) setSelected(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to delete package");
    }
  }

  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: PackagePublic[]; total: number }> => {
      if (hasPinFilter) {
        const res = await api.getPackages();
        const scoped = res.packages.filter((p) => activeOrgIds.includes(p.organization_id));
        return applyClientListParams(scoped, params, {
          searchFields: (p) => [p.name, p.description, p.organization_name],
          sortAccessors: {
            name: (p) => p.name,
            status: (p) => p.status,
            organization: (p) => p.organization_name,
          },
          defaultSort: "name",
        });
      }
      const res = await api.getPackages({
        page: params.page, per_page: params.per_page,
        search: params.search, sort: params.sort, order: params.order,
      });
      return { data: res.packages, total: res.total ?? res.packages.length };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [pinKey],
  );

  const columns: DataTableColumn<PackagePublic>[] = [
    {
      key: "name", header: "Name", sortable: true, className: "font-medium",
      render: (p) => <Link to={`/packages/${p.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{p.name}</Link>,
    },
    { key: "organization", header: "Organization", sortable: true, className: "text-muted-foreground", render: (p) => p.organization_name },
    { key: "status", header: "Status", sortable: true, render: (p) => <span className={statusBadge[p.status] ?? statusBadge.draft}>{p.status}</span> },
    { key: "placements", header: "Placements", className: "text-muted-foreground", render: (p) => p.placement_count },
    {
      key: "actions", header: "", headerClassName: "w-36",
      render: (pkg) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {pkg._actions.includes("edit") && (
            <Link to={`/packages/${pkg.id}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })}>
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {pkg._actions.includes("delete") && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full gap-0" data-page="packages-list" data-state={tableState}>
      <div className="min-w-0 flex-1 flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Packages</h1>
            <p className="text-muted-foreground">Bundle advertising spots into sellable packages</p>
          </div>
          <div className="flex gap-2">
            <Link to="/packages/new" className={buttonVariants({ size: "sm" })} data-action="create">
              <Plus className="mr-2 h-4 w-4" />
              New Package
            </Link>
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
        </div>

        <PinFilterBar entityType="Organization" />

        <div data-region="results">
          <DataTable
            columns={columns}
            fetcher={fetcher}
            fetchKey={`${pinKey}:${refreshTick}`}
            rowKey={(p) => p.id}
            rowProps={(p) => ({ "data-item": "package", "data-item-id": p.id })}
            rowClassName={(p) => (selected?.id === p.id ? "bg-muted" : undefined)}
            searchPlaceholder="Search packages..."
            defaultSort="name"
            emptyMessage="No packages yet."
            onRowClick={(p) => setSelected(p)}
            onStateChange={setTableState}
          />
        </div>
      </div>
      <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="Package">
        {selected && (
          <>
            <PreviewField label="Name">{selected.name}</PreviewField>
            <PreviewField label="Organization">{selected.organization_name}</PreviewField>
            <PreviewField label="Status">
              <span className={statusBadge[selected.status] ?? statusBadge.draft}>{selected.status}</span>
            </PreviewField>
            <PreviewField label="Placements">{selected.placement_count}</PreviewField>
          </>
        )}
      </PreviewPane>
    </div>
  );
}
